import * as React from "react";

import { Box } from "../../components/ui/box";
import { WASH_HOVER } from "../../components/ui/box-classes.ts";
import { Input } from "../../components/ui/input";
import { ScrollArea } from "../../components/ui/scroll-area.tsx";
import { Text, textVariants, type TextProps } from "../../components/ui/text";
import { Textarea } from "../../components/ui/textarea";
import { useMenuFocusStealGuard } from "../../hooks/use-menu-focus-steal-guard";
import { cn } from "../../lib/utils";
import type { Omittable } from "../../lib/props";

export type EditableTitleFieldProps = Omittable<{
  /** Current committed title, rendered as plain text at rest. */
  value: string;
  /** Persist the trimmed next value when the edit commits (Enter/blur). */
  onCommit: (next: string) => void;
  size?: TextProps["size"];
  weight?: TextProps["weight"];
  align?: TextProps["align"];
  variant?: TextProps["variant"];
  /** Span the available width (card header, pushing controls out); omit to
   *  hug the text (dialog breadcrumb / heading). */
  fill?: boolean;
  /** Pull the field left by its horizontal hover padding so the title text
   *  aligns flush with the content below it, while the hover/edit outline
   *  still bleeds the same 4px outward. Used in the card header. The `wash`
   *  chrome bleeds on every side by itself and ignores this. */
  flushLeft?: boolean;
  /** When false, renders plain non-interactive text with no edit affordance. */
  editable?: boolean;
  chrome?: "outline" | "wash" | "none";
  restAs?: "button" | "text";
  truncate?: TextProps["truncate"];
  placeholder?: string;
  "aria-label"?: string;
  /** Controlled editing, for a host whose MENU launches the edit (a card
   *  menu's Rename): the host owns when editing is on, and the field guards
   *  its first blur against the closing menu's own focus traffic. Omit for
   *  the normal click-to-edit field. */
  editing?: boolean;
  onEditingChange?: (editing: boolean) => void;
  /** Edit as a wrapping block instead of a one-line input: the editor keeps
   *  the resting text's line boxes (a wrapped title stays wrapped, the caret
   *  lands in flowed text) and auto-grows with its content. Enter still
   *  commits — a title holds no newline. For hosts whose resting text wraps
   *  (a card title on a narrow column). */
  multiline?: boolean;
  newlines?: boolean;
  maxHeight?: number;
  trailing?: React.ReactNode;
}>;

/**
 * An inline-editable title: plain text at rest that reveals its chrome on
 * hover — a rounded input-style outline, or the hover wash (`chrome`) — and
 * keeps the editing chrome lit while it's being edited (even after the
 * pointer leaves) so the spot being edited stays obvious. The border colors
 * in from a permanently-reserved transparent 1px, so nothing reflows.
 * Enter/blur commit the trimmed value; Escape cancels, and stops there.
 *
 * Used by widget cards and detail dialogs so every editable title reads the
 * same; `fill` adapts it to a full-width header vs. a content-hugging title.
 */
export function EditableTitleField({
  value,
  onCommit,
  size = "sm",
  weight = "semibold",
  align,
  variant,
  fill,
  flushLeft,
  editable = true,
  chrome = "outline",
  restAs = "button",
  truncate = true,
  placeholder = "Untitled",
  "aria-label": ariaLabel,
  editing: controlledEditing,
  onEditingChange,
  multiline,
  newlines,
  maxHeight,
  trailing,
}: EditableTitleFieldProps) {
  const [uncontrolledEditing, setUncontrolledEditing] = React.useState(false);
  const isEditingControlled = controlledEditing !== undefined;
  const editing = isEditingControlled ? controlledEditing : uncontrolledEditing;
  const [draft, setDraft] = React.useState(value);
  const didSubmitRef = React.useRef(false);
  // Controlled editing was launched from outside (a menu's Rename), so its
  // first blur can be the closing menu's own focus traffic.
  const reclaimFromMenuSteal = useMenuFocusStealGuard(
    isEditingControlled && editing,
  );

  // The controlled field mounts already editing, so the draft seeds at
  // mount; the value effect below keeps it honest when editing is off.
  React.useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  const setEditing = (nextEditing: boolean) => {
    if (!isEditingControlled) setUncontrolledEditing(nextEditing);
    onEditingChange?.(nextEditing);
  };

  const startEditing = () => {
    if (!editable) return;
    didSubmitRef.current = false;
    setDraft(value);
    setEditing(true);
  };

  const finishEditing = (next: string) => {
    setEditing(false);
    const trimmed = next.trim();
    if (trimmed !== value) onCommit(trimmed);
  };

  const cancelEditing = () => {
    setDraft(value);
    setEditing(false);
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (event.key === "Escape") {
      // Consumed so an enclosing dialog/popover doesn't also close on Escape.
      event.stopPropagation();
      cancelEditing();
    }
    if (event.key === "Enter") {
      if (newlines && multiline && !event.metaKey && !event.ctrlKey) return;
      // A title holds no newline — Enter commits in the multiline editor too.
      event.preventDefault();
      didSubmitRef.current = true;
      finishEditing(draft);
    }
  };

  const editorInk = variant === undefined ? "text-foreground" : undefined;
  const outlined = chrome === "outline";
  const washed = chrome === "wash";
  const resting = (
    <Text
      size={size}
      weight={weight}
      align={align}
      variant={value.length > 0 ? variant : "subtle"}
      truncate={truncate}
      pre={newlines && multiline ? true : undefined}
    >
      {value.length > 0 ? value : placeholder}
    </Text>
  );
  const clamp = (body: React.ReactNode) =>
    multiline && maxHeight !== undefined ? (
      <ScrollArea
        scrollFade
        style={{ maxHeight, ["--overflow-gutter" as string]: `${maxHeight / 2}px` }}
      >
        <Box
          direction="row"
          className="[[data-has-overflow-y]>[data-slot=scroll-area-content]>&]:pb-(--overflow-gutter)"
        >
          {body}
        </Box>
      </ScrollArea>
    ) : (
      body
    );
  const restingRow = trailing ? (
    <Box minWidth={0} direction="row" align="center" gap="2xs">
      {resting}
      {trailing}
    </Box>
  ) : (
    resting
  );

  return (
    // Flex rows, never blocks: `truncate` is overflow + ellipsis, and an
    // inline span ignores both — the glyphs paint straight past the box. As a
    // flex item the Text span blockifies (and its overflow lifts the automatic
    // minimum size), so a long title clips inside the field instead of running
    // under whatever sits to its right.
    <Box minWidth={0} direction="row" align="center" rounded="md" paddingX="xs" paddingY="2xs"
      className={cn(
        // Chrome-less hosts render bare Text at rest, so no reserved border (glyphs would shift).
        chrome !== "none" && "border border-transparent",
        // Bleed padding+border back out so the box matches bare text; flex-1, not 100% width (margins).
        washed && "-mx-[5px] -my-[3px]",
        fill && (washed ? "flex-1" : "w-full flex-1"),
        flushLeft && !washed && "-ml-1",
        outlined && editable && !editing && "hover:border-input",
        outlined && editing && "border-ring",
        washed && editable && !editing && WASH_HOVER,
        washed && editing && "border-border-strong",
        editing && "cursor-text",
      )}
    >
      {clamp(editing && multiline ? (
        // The wrapping editor: a bare Textarea wearing the SAME text classes
        // as the resting Text, so the wrapped title keeps its line boxes and
        // the caret lands in flowed text. `field-sizing-content` grows it
        // with the draft (the primitive's own sizing mechanism); Enter
        // commits via handleKeyDown, so no newline lands unless `newlines`.
        <Textarea
          bare
          value={draft}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
            setDraft(event.target.value)
          }
          onBlur={(event: React.FocusEvent<HTMLTextAreaElement>) => {
            if (reclaimFromMenuSteal(event)) return;
            if (!didSubmitRef.current) finishEditing(draft);
          }}
          onKeyDown={handleKeyDown}
          onClick={(event: React.MouseEvent) => event.stopPropagation()}
          onFocus={(event: React.FocusEvent<HTMLTextAreaElement>) => {
            // A textarea autofocuses with the caret at the START; the rename
            // gesture expects it at the end, where typing extends the title.
            const node = event.currentTarget;
            node.setSelectionRange(node.value.length, node.value.length);
          }}
          placeholder={placeholder}
          autoFocus
          rows={1}
          className={cn(
            textVariants({ align, size, weight, variant }),
            editorInk,
            "field-sizing-content w-full min-w-0 resize-none bg-transparent outline-none placeholder:text-muted-foreground/60",
          )}
          aria-label={ariaLabel ?? "Edit title"}
        />
      ) : editing ? (
        // A bare Input wearing the SAME text classes as the resting Text (via
        // textVariants), so entering edit mode swaps the glyphs' owner without
        // changing their size, weight, or line box — the row never grows.
        // `bare` drops the primitive's shell and fixed metrics entirely.
        <Input
          bare
          value={draft}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setDraft(event.target.value)
          }
          onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
            if (reclaimFromMenuSteal(event)) return;
            if (!didSubmitRef.current) finishEditing(draft);
          }}
          onKeyDown={handleKeyDown}
          onClick={(event: React.MouseEvent) => event.stopPropagation()}
          placeholder={placeholder}
          autoFocus
          className={cn(
            textVariants({ align, size, weight, variant }),
            editorInk,
            "w-full min-w-0 bg-transparent outline-none placeholder:text-muted-foreground/60",
          )}
          aria-label={ariaLabel ?? "Edit title"}
        />
      ) : editable && restAs === "text" ? (
        // grow, never fill: a zero flex basis sizes a hugging field to its longest word.
        <Box grow minWidth={0} direction="row" align="center" cursor="text"
          onClick={startEditing}
        >
          {restingRow}
        </Box>
      ) : editable ? (
        <Box grow minWidth={0} direction="row" align="center" cursor="text"
          as="button"
          className="text-left"
          onClick={startEditing}
          aria-label={ariaLabel ?? "Edit title"}
        >
          {restingRow}
        </Box>
      ) : (
        <Box minWidth={0} direction="row" align="center" gap="2xs">
          <Text size={size} weight={weight} align={align} variant={variant} truncate={truncate}>
            {value}
          </Text>
          {trailing}
        </Box>
      ))}
    </Box>
  );
}
